import type { PostHog } from 'posthog-js'
import { Id } from '@/infra/id'
import type { WorkspaceMember } from '@/features/iam/members/WorkspaceMember'
import type { UserData } from '@/features/iam/users/UserData'
import type { TrackingService } from '@/features/tracking/TrackingService'

export class PostHogTrackingService implements TrackingService {
  /** @inheritDoc */
  identify(member: WorkspaceMember, userData: UserData) {
    this._call((postHog) => {
      postHog.identify(Id.assert(member.user.id), {
        first_name: member.user.givenName,
        last_name: member.user.familyName,
        name: member.user.displayName,
        email: member.user.email,
        workspace_id: Id.assert(member.workspace.id),
        id: Id.assert(member.user.id),
        date_created: member.dateCreated?.toISOString() ?? null,
        last_login: member.lastLogin?.toISOString() ?? null,
        user_type: userData.type,
        workspace_name: member.workspace.name,
      })
    })
  }

  /** @inheritDoc */
  reset() {
    this._call((postHog) => {
      postHog.stopSessionRecording()
      postHog.reset()
    })
  }

  /**
   * Calls the specified function on the PostHog SDK, which is initialized
   * by the inline snippet in the server-rendered app view. Calls made
   * before the SDK finishes loading are queued by the snippet stub.
   *
   * @param fn
   * @private
   */
  private _call(fn: (postHog: PostHog) => unknown) {
    if (window.posthog) {
      fn(window.posthog)
    }
  }

  /**
   * Creates the PostHog tracking service.
   */
  static create() {
    return new PostHogTrackingService()
  }
}
